import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Plus, Play, RefreshCcw, AlertTriangle } from "lucide-react";
import { FieldMapper } from "@/components/FieldMapper";
import { WebinarSourceBinding } from "@/components/webinar/WebinarSourceBinding";

const copy = async (t, w) => { try { await navigator.clipboard.writeText(t); toast.success(`${w} copied`); } catch { toast.error("Copy failed"); } };

/* ---------------------- SHARED PRODUCT ALIAS MAPPER --------------------- */
/* One global table (product_intake_aliases), shared by every mixed-product
   source (Website, Sheets, any future one) — reused here rather than
   duplicated per source, matching the backend's own product_routing.py
   design. An incoming value NOT listed here is always Unclassified —
   SalesOS never guesses. */
export const ProductAliasMapper = ({ products }) => {
  const [aliases, setAliases] = useState(null);
  const [newRaw, setNewRaw] = useState("");
  const [newProduct, setNewProduct] = useState("");
  const load = useCallback(() => api.get("/admin/product-aliases").then((r) => setAliases(r.data)).catch(() => setAliases({})), []);
  useEffect(() => { load(); }, [load]);
  const setAlias = async (rawValue, productCode) => {
    try {
      await api.put("/admin/product-aliases", { raw_value: rawValue, product_code: productCode || null });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const addRow = () => {
    if (!newRaw.trim() || !newProduct) return;
    setAlias(newRaw.trim(), newProduct);
    setNewRaw(""); setNewProduct("");
  };
  if (aliases === null) return <Skeleton className="h-24" />;
  const rows = Object.entries(aliases);
  return (
    <div className="space-y-2" data-testid="product-alias-mapper">
      <Label className="text-xs">Incoming value → SalesOS Product</Label>
      <p className="text-[11px] text-muted-foreground">Shared by every mixed-product source (Website, Sheets). A value not listed here always stays <b>Unclassified</b> — never guessed.</p>
      {rows.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <TableHeader><TableRow><TableHead>Incoming value</TableHead><TableHead>SalesOS Product</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map(([raw, code]) => (
                <TableRow key={raw} data-testid={`alias-row-${raw}`}>
                  <TableCell className="text-xs font-mono">{raw}</TableCell>
                  <TableCell>
                    <Select value={code} onValueChange={(v) => setAlias(raw, v)}>
                      <SelectTrigger className="h-8 text-xs w-56" data-testid={`alias-target-${raw}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[hsl(var(--danger))]" onClick={() => setAlias(raw, null)}>Remove → Unclassified</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="flex gap-2 items-end flex-wrap">
        <div className="space-y-1 flex-1 min-w-40">
          <Label className="text-[11px] text-muted-foreground">New incoming value (exact text)</Label>
          <Input data-testid="alias-new-raw" className="h-8 text-xs" value={newRaw} onChange={(e) => setNewRaw(e.target.value)} placeholder='e.g. "Student Visa"' />
        </div>
        <div className="space-y-1 w-56">
          <Label className="text-[11px] text-muted-foreground">Maps to</Label>
          <Select value={newProduct} onValueChange={setNewProduct}>
            <SelectTrigger data-testid="alias-new-product" className="h-8 text-xs"><SelectValue placeholder="Pick product" /></SelectTrigger>
            <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="sm" className="h-8" data-testid="alias-add" disabled={!newRaw.trim() || !newProduct} onClick={addRow}>Add</Button>
      </div>
    </div>
  );
};

/* ------------------------- ROLES & PERMISSIONS ------------------------- */
const AddRoleDialog = ({ catalog, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [caps, setCaps] = useState([]);
  const [saving, setSaving] = useState(false);
  const toggleCap = (cap) => setCaps((c) => c.includes(cap) ? c.filter((x) => x !== cap) : [...c, cap]);
  const submit = async () => {
    if (!name.trim()) { toast.error("Role name required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/roles-matrix", { role: name.trim(), capabilities: caps });
      toast.success(`Role "${data.role}" created`);
      setOpen(false); setName(""); setCaps([]);
      onCreated();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  return (
    <>
      <Button data-testid="add-role-btn" size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add role
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create a new role</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Role name</Label>
            <Input data-testid="new-role-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. sales_lead" />
            <p className="text-xs text-muted-foreground mt-1">Lowercase letters, numbers, underscores only.</p>
          </div>
          <div>
            <Label className="text-xs">Starting capabilities</Label>
            <div className="max-h-64 overflow-y-auto border rounded-md p-2 mt-1 space-y-1">
              {catalog.map((cap) => (
                <label key={cap} className="flex items-center gap-2 text-xs font-mono cursor-pointer">
                  <Checkbox checked={caps.includes(cap)} onCheckedChange={() => toggleCap(cap)} />
                  {cap}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="create-role-submit" onClick={submit} disabled={saving}>Create role</Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
    </>
  );
};

export const RolesTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/roles-matrix").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (role, cap, has) => {
    const r = data.roles.find((x) => x.role === role);
    const caps = has ? r.capabilities.filter((c) => c !== cap) : [...r.capabilities, cap];
    try {
      await api.put(`/admin/roles-matrix/${role}`, { capabilities: caps });
      toast.success(`${role}: ${cap} ${has ? "removed" : "granted"}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  if (!data) return <Skeleton className="h-64" />;
  const roles = data.roles.filter((r) => r.role !== "admin");
  return (
    <Card data-testid="roles-matrix">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm font-heading">Roles & permissions matrix</CardTitle>
          <p className="text-xs text-muted-foreground">Enforced server-side on every API call. The admin role always retains full access.</p>
        </div>
        <AddRoleDialog catalog={data.catalog} onCreated={load} />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-44">Capability</TableHead>
              {roles.map((r) => <TableHead key={r.role} className="capitalize text-center">{r.role}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.catalog.map((cap) => (
              <TableRow key={cap}>
                <TableCell className="font-mono text-xs">{cap}</TableCell>
                {roles.map((r) => {
                  const has = r.capabilities.includes(cap) || r.capabilities.includes("*");
                  return (
                    <TableCell key={r.role} className="text-center">
                      <Checkbox data-testid={`perm-${r.role}-${cap}`} checked={has}
                        onCheckedChange={() => toggle(r.role, cap, has)} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

/* ------------------------------- TAGS --------------------------------- */
export const TagsTab = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", group: "General", color: "slate" });
  const load = useCallback(() => api.get("/tags").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/tags", form); toast.success("Tag created"); setForm({ ...form, name: "" }); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!data) return <Skeleton className="h-48" />;
  const byGroup = data.tags.reduce((m, t) => { (m[t.group] = m[t.group] || []).push(t); return m; }, {});
  return (
    <div className="space-y-4" data-testid="tags-admin">
      <Card><CardContent className="pt-4 flex items-end gap-2 flex-wrap">
        <div className="space-y-1"><Label className="text-xs">Tag name</Label>
          <Input data-testid="tag-name-input" className="h-9 w-44" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">Group</Label>
          <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
            <SelectTrigger data-testid="tag-group-select" className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{(data.groups || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label className="text-xs">Colour</Label>
          <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
            <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{["slate", "red", "amber", "green", "sky", "violet"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select></div>
        <Button data-testid="tag-create-button" className="h-9" disabled={!form.name.trim()} onClick={create}><Plus className="h-4 w-4 mr-1" /> Add tag</Button>
      </CardContent></Card>
      {Object.entries(byGroup).map(([g, tags]) => (
        <Card key={g}><CardContent className="pt-4">
          <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">{g}</p>
          <div className="flex gap-2 flex-wrap">
            {tags.map((t) => (
              <Badge key={t.tag_id} variant="outline" data-testid={`tag-${t.name.replace(/\s/g, '-')}`}
                className={`text-xs ${t.active === false ? "opacity-40" : ""}`}>
                <span className={`h-2 w-2 rounded-full mr-1.5 bg-${t.color}-500`} style={{ backgroundColor: { slate: "#94a3b8", red: "#ef4444", amber: "#f59e0b", green: "#10b981", sky: "#0ea5e9", violet: "#8b5cf6" }[t.color] }} />
                {t.name}
              </Badge>
            ))}
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
};

/* --------------------------- CUSTOM FIELDS ----------------------------- */
export const FieldsTab = () => {
  const [fields, setFields] = useState(null);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", type: "text", product_code: null, section: "Profile", required: false, options: null, entity: "contact" });
  const load = useCallback(() => api.get("/custom-fields/all").then((r) => setFields(r.data)), []);
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)); }, [load]);
  const create = async () => {
    try {
      const body = { ...form, options: form.type === "dropdown" && form.options ? String(form.options).split(",").map((s) => s.trim()) : null };
      await api.post("/custom-fields", body);
      toast.success("Field created"); setOpen(false); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  if (!fields) return <Skeleton className="h-48" />;
  return (
    <div className="space-y-3" data-testid="fields-admin">
      <div className="flex justify-end">
        <Button data-testid="field-new-button" size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> New field</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Label</TableHead><TableHead>Key</TableHead><TableHead>Type</TableHead>
            <TableHead>Product</TableHead><TableHead>Section</TableHead><TableHead>Mapping key</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {fields.map((f) => (
              <TableRow key={f.field_id} data-testid={`field-row-${f.key}`}>
                <TableCell className="text-sm">{f.label}{f.required && " *"}</TableCell>
                <TableCell className="font-mono text-xs">{f.key}</TableCell>
                <TableCell className="text-xs">{f.type}</TableCell>
                <TableCell className="text-xs">{f.product_code || "All"}</TableCell>
                <TableCell className="text-xs">{f.section}</TableCell>
                <TableCell className="font-mono text-xs">{f.mapping_key || f.key}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">New custom field</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Label</Label>
              <Input data-testid="field-label" className="h-9" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} /></div>
            <div className="space-y-1"><Label className="text-xs">Key (mapping)</Label>
              <Input data-testid="field-key" className="h-9 font-mono" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="field-type" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{["text", "number", "date", "dropdown", "multiselect", "boolean", "longtext", "country", "currency"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Product</Label>
              <Select value={form.product_code || "all"} onValueChange={(v) => setForm({ ...form, product_code: v === "all" ? null : v })}>
                <SelectTrigger data-testid="field-product" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All products</SelectItem>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Section</Label>
              <Input data-testid="field-section" className="h-9" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} /></div>
            {form.type === "dropdown" && (
              <div className="space-y-1"><Label className="text-xs">Options (comma-separated)</Label>
                <Input data-testid="field-options" className="h-9" onChange={(e) => setForm({ ...form, options: e.target.value })} /></div>
            )}
            <label className="flex items-center gap-2 text-xs col-span-2">
              <Checkbox checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: !!v })} /> Required field
            </label>
          </div>
          <DialogFooter><Button data-testid="field-create" disabled={!form.label.trim()} onClick={create}>Create field</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ----------------------- WEBHOOKS / API CONNECTIONS -------------------- */
const ApiKeysCard = () => {
  const [keys, setKeys] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [caps, setCaps] = useState([]);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/api-keys").then((r) => setKeys(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/roles-matrix").then((r) => setCatalog(r.data.catalog)); }, []);

  const toggleCap = (cap) => setCaps((c) => c.includes(cap) ? c.filter((x) => x !== cap) : [...c, cap]);
  const generate = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const { data: d } = await api.post("/admin/api-keys", { name: name.trim(), capabilities: caps });
      setCreated(d);
      setName(""); setCaps([]);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  const revoke = async (keyId) => {
    try { await api.post(`/admin/api-keys/${keyId}/revoke`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  if (!keys) return <Skeleton className="h-40" />;
  return (
    <Card data-testid="api-keys-admin">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm font-heading">API keys</CardTitle>
          <p className="text-xs text-muted-foreground">General programmatic access to the SalesOS API. Send <code className="font-mono">Authorization: Bearer &lt;key&gt;</code> — a key can only do what its capabilities allow, enforced the same way as any employee role.</p>
        </div>
        <Button data-testid="generate-api-key-btn" size="sm" variant="outline" onClick={() => { setCreated(null); setOpen(true); }}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Generate API key
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {keys.length === 0 && <p className="text-xs text-muted-foreground">No API keys yet.</p>}
        {keys.map((k) => (
          <div key={k.key_id} className="p-2 rounded border text-xs flex items-center gap-2 flex-wrap" data-testid={`api-key-${k.key_id}`}>
            <b>{k.name}</b>
            <code className="font-mono bg-muted px-1 rounded">apk_••••{k.preview}</code>
            <Badge variant="outline" className="text-[9px]">{k.capabilities.length} capabilities</Badge>
            <span className="text-muted-foreground">{k.last_used_at ? `last used ${fmtIST(k.last_used_at)}` : "never used"}</span>
            {k.revoked
              ? <Badge variant="outline" className="text-[9px] ml-auto">revoked</Badge>
              : <Button data-testid={`revoke-api-key-${k.key_id}`} size="sm" variant="outline" className="h-6 text-[10px] ml-auto" onClick={() => revoke(k.key_id)}>Revoke</Button>}
          </div>
        ))}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Generate API key</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-2">
              <div className="p-2 rounded bg-[hsl(var(--warning))]/10 text-xs space-y-1" data-testid="api-key-created-secret">
                <p><b>Key (shown once — copy it now):</b></p>
                <p className="flex items-center gap-1"><code className="font-mono break-all">{created.key}</code>
                  <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copy(created.key, "API key")}><Copy className="h-3 w-3" /></Button></p>
              </div>
              <DialogFooter><Button onClick={() => setOpen(false)}>Done</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Key name</Label>
                <Input data-testid="new-api-key-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. BI dashboard" />
              </div>
              <div>
                <Label className="text-xs">Capabilities</Label>
                <div className="max-h-64 overflow-y-auto border rounded-md p-2 mt-1 space-y-1">
                  {catalog.map((cap) => (
                    <label key={cap} className="flex items-center gap-2 text-xs font-mono cursor-pointer">
                      <Checkbox checked={caps.includes(cap)} onCheckedChange={() => toggleCap(cap)} />
                      {cap}
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="create-api-key-submit" onClick={generate} disabled={saving}>Generate</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const ConnectionsTab = () => {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [inForm, setInForm] = useState({ name: "", target: "lead_create", product_code: "", source: "webhook", mapping: null, test_mode: false, active: true });
  const [outForm, setOutForm] = useState({ name: "", event: "lead_created", url: "", method: "POST", secret: "", active: true });
  const [created, setCreated] = useState(null);
  const [leadSecret, setLeadSecret] = useState("");
  const [inbox, setInbox] = useState([]);
  const load = useCallback(() => {
    api.get("/connections").then((r) => setData(r.data));
    api.get("/connections/logs?limit=20").then((r) => setLogs(r.data));
    api.get("/webhooks/inbox?limit=30").then((r) => setInbox(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveLeadSecret = async (value) => {
    try {
      await api.put("/admin/integrations/meta_events", { config: { "webhook.secret": value } });
      toast.success("Lead webhook secret saved");
      setLeadSecret(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const toggleLeadIntake = async (enabled) => {
    try {
      await api.put("/admin/config", { key: "webhook.lead_intake_enabled", value: enabled });
      toast.success(enabled ? "Lead intake enabled — new deliveries will be processed"
                            : "Lead intake disabled — deliveries acknowledged but ignored, independent of Meta's own switches");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const generateLeadSecret = () => {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const key = btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "").slice(0, 32);
    saveLeadSecret(key);
    copy(key, "New lead webhook secret (share it with Pabbly/n8n now — shown masked after saving)");
  };

  const createIn = async () => {
    try {
      const { data: d } = await api.post("/connections/inbound", inForm);
      setCreated(d); toast.success("Inbound endpoint created — copy the secret now");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const createOut = async () => {
    try { await api.post("/connections/outbound", outForm); toast.success("Outbound webhook created"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const testOut = async (id) => {
    try {
      const { data: d } = await api.post(`/connections/outbound/${id}/test`);
      d.ok ? toast.success(`Test delivered: ${d.detail}`) : toast.error(`Test failed: ${d.detail}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  if (!data) return <Skeleton className="h-64" />;
  return (
    <div className="space-y-4" data-testid="connections-admin">
      <ApiKeysCard />
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pabbly / n8n / Generic automation</CardTitle>
        <p className="text-xs text-muted-foreground">Connect any tool without custom code: inbound endpoints receive data with a secret header; outbound webhooks push SalesOS events with retries and idempotency. Nothing is hard-coded to one provider.</p>
      </CardHeader>
        <CardContent className="text-xs space-y-2">
          <div>
            <p>Lead intake (Pabbly/n8n bridge — also accepts Meta lead payloads shaped this way): <code className="font-mono bg-muted px-1 rounded">{data.lead_webhook_url}</code>
              <Button variant="ghost" size="sm" className="h-5 px-1 ml-1" onClick={() => copy(data.lead_webhook_url, "URL")}><Copy className="h-3 w-3" /></Button></p>
            <p className="text-muted-foreground">Header <code className="font-mono">X-Hook-Secret</code> (custom endpoints) / <code className="font-mono">X-Webhook-Secret</code> (lead intake). Include a unique <code className="font-mono">external_event_id</code> per record. This endpoint has no Meta-specific dependency — it works independently of anything under Admin → Meta/Facebook.</p>
          </div>
          <div className="flex items-center gap-2 pt-1 border-t" data-testid="lead-intake-toggle-row">
            <Switch data-testid="lead-intake-enabled-switch" checked={data.lead_webhook_intake_enabled !== false}
              onCheckedChange={toggleLeadIntake} />
            <Label className="text-xs">Lead intake enabled</Label>
            <span className="text-[10px] text-muted-foreground">Off = deliveries are acknowledged but ignored (no retry backlog) — independent of Admin → Meta/Facebook's own switches.</span>
          </div>
          <div className="flex items-end gap-2 flex-wrap pt-1 border-t">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1.5">
                Lead webhook shared secret (X-Webhook-Secret value)
                {data.lead_webhook_secret_configured && <span className="text-emerald-600">✓ configured</span>}
              </Label>
              <Input data-testid="lead-webhook-secret-input" type="password" className="h-8 w-64 text-xs font-mono"
                placeholder={data.lead_webhook_secret_configured ? "•••• configured" : "not set"}
                value={leadSecret} onChange={(e) => setLeadSecret(e.target.value)} />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!leadSecret} onClick={() => saveLeadSecret(leadSecret)}>Save</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={generateLeadSecret}>
              <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Generate new secret
            </Button>
          </div>
          <div className="pt-2 border-t space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground uppercase">Recent deliveries to this endpoint</p>
            {inbox.length === 0 && <p className="text-muted-foreground">None yet.</p>}
            {inbox.map((w) => (
              <div key={w.inbox_id} className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`webhook-inbox-${w.inbox_id}`}>
                <span className="text-muted-foreground">{fmtIST(w.received_at)}</span>
                <Badge variant="outline" className="text-[9px]">{w.provider || "unknown source"}</Badge>
                <span className="text-muted-foreground font-mono">{(w.payload_keys || []).join(", ")}</span>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground">Every delivery here is independent of Admin → Meta/Facebook's own switches — if leads keep arriving with those off, check whether a Pabbly/n8n automation is still sending to this endpoint.</p>
          </div>
        </CardContent>
      </Card>

      {/* Inbound */}
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Inbound endpoints</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.inbound.map((i) => (
            <div key={i.endpoint_id} className="p-2 rounded border text-xs space-y-2">
              <div className="flex items-center gap-2 flex-wrap" data-testid={`inbound-${i.endpoint_id}`}>
                <b>{i.name}</b>
                <Badge variant="outline" className="text-[9px]">{i.target}</Badge>
                {i.test_mode && <Badge variant="outline" className="text-[9px]">test mode</Badge>}
                <code className="font-mono bg-muted px-1 rounded truncate max-w-64">{i.url}</code>
                <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copy(i.url, "Endpoint URL")}><Copy className="h-3 w-3" /></Button>
                <span className="text-muted-foreground ml-auto">{i.active ? "active" : "off"}</span>
              </div>
              {i.target === "lead_create" && (
                <WebinarSourceBinding sourceType="webhook_endpoint" sourceId={i.endpoint_id}
                                      defaultProduct={i.product_code} />
              )}
            </div>
          ))}
          <div className="flex items-end gap-2 flex-wrap pt-2 border-t">
            <div className="space-y-1"><Label className="text-xs">Name</Label>
              <Input data-testid="inbound-name" className="h-8 w-40 text-xs" value={inForm.name} onChange={(e) => setInForm({ ...inForm, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Purpose</Label>
              <Select value={inForm.target} onValueChange={(v) => setInForm({ ...inForm, target: v })}>
                <SelectTrigger data-testid="inbound-target" className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead_create">Lead creation</SelectItem>
                  <SelectItem value="customer_update">Customer update</SelectItem>
                  <SelectItem value="appointment_note">Appointment note</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Default product</Label>
              <Input data-testid="inbound-product" className="h-8 w-36 text-xs font-mono" placeholder="SA_PREMIUM" value={inForm.product_code} onChange={(e) => setInForm({ ...inForm, product_code: e.target.value })} /></div>
            <label className="flex items-center gap-1.5 text-xs pb-2"><Checkbox checked={inForm.test_mode} onCheckedChange={(v) => setInForm({ ...inForm, test_mode: !!v })} /> Test mode</label>
            <Button data-testid="inbound-create" size="sm" className="h-8" disabled={!inForm.name.trim()} onClick={createIn}>Create endpoint</Button>
          </div>
          {created && (
            <div className="p-2 rounded bg-[hsl(var(--warning))]/10 text-xs space-y-1" data-testid="inbound-created-secret">
              <p><b>Secret (shown once):</b> <code className="font-mono">{created.secret}</code>
                <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copy(created.secret, "Secret")}><Copy className="h-3 w-3" /></Button></p>
              <p>URL: <code className="font-mono">{created.url}</code></p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outbound */}
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Outbound webhooks (WHEN event → SEND to URL)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.outbound.map((o) => (
            <div key={o.webhook_id} className="p-2 rounded border text-xs flex items-center gap-2 flex-wrap" data-testid={`outbound-${o.webhook_id}`}>
              <b>{o.name}</b>
              <Badge variant="outline" className="text-[9px]">{o.event}</Badge>
              <code className="font-mono bg-muted px-1 rounded truncate max-w-56">{o.url}</code>
              <Button data-testid={`outbound-test-${o.webhook_id}`} size="sm" variant="outline" className="h-6 text-[10px] ml-auto" onClick={() => testOut(o.webhook_id)}>Test send</Button>
            </div>
          ))}
          <div className="flex items-end gap-2 flex-wrap pt-2 border-t">
            <div className="space-y-1"><Label className="text-xs">Name</Label>
              <Input data-testid="outbound-name" className="h-8 w-36 text-xs" value={outForm.name} onChange={(e) => setOutForm({ ...outForm, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Event</Label>
              <Select value={outForm.event} onValueChange={(v) => setOutForm({ ...outForm, event: v })}>
                <SelectTrigger data-testid="outbound-event" className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{(data.events || []).map((e) => <SelectItem key={e} value={e}>{e.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Target URL</Label>
              <Input data-testid="outbound-url" className="h-8 w-64 text-xs font-mono" placeholder="https://connect.pabbly.com/…" value={outForm.url} onChange={(e) => setOutForm({ ...outForm, url: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Secret (optional)</Label>
              <Input data-testid="outbound-secret" className="h-8 w-32 text-xs font-mono" value={outForm.secret} onChange={(e) => setOutForm({ ...outForm, secret: e.target.value })} /></div>
            <Button data-testid="outbound-create" size="sm" className="h-8" disabled={!outForm.name.trim() || !outForm.url.startsWith("http")} onClick={createOut}>Create webhook</Button>
          </div>
        </CardContent>
      </Card>

      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Webhook logs</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {logs.length === 0 && <p className="text-xs text-muted-foreground">No webhook traffic yet.</p>}
          {logs.map((l) => (
            <p key={l.log_id} className="text-xs">
              <span className="text-muted-foreground">{fmtIST(l.at)}</span> · <Badge variant="outline" className="text-[9px]">{l.direction}</Badge> {l.event} → <b>{l.status}</b> {l.detail && <span className="text-muted-foreground">({String(l.detail).slice(0, 60)})</span>}
            </p>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

/* --------------------------- GOOGLE SHEETS ----------------------------- */
const SHEETS_UNIVERSAL_TARGETS = [
  ["name", "Contact — Full name"], ["phone", "Contact — Phone"], ["email", "Contact — Email"],
  ["city", "Contact — City"], ["product_code", "Attribution — Product code"],
  ["source", "Attribution — Source"], ["campaign", "Attribution — Campaign"],
];

const SHEET_INTAKE_MODES = [["single", "One particular product"], ["mixed", "Multiple products / Mixed leads"]];

/* First-enable scan → Import Existing / Start From Now — one connection
   card manages its own scan state. A connection with intake_started=false
   can ONLY scan/activate; nothing is ever imported before an admin makes
   an explicit choice here. */
/* Launch-day catch-up backfill (2026-09 lead-intake audit, section C) — a
   bounded, dry-run-first recovery for rows that arrived BEFORE this
   connection was activated. Deliberately requires an explicit Preview
   before Execute even unlocks — never a one-click way to run it blind. */
const SheetBackfillDialog = ({ connectionId, open, onOpenChange, onDone }) => {
  const [fromRow, setFromRow] = useState("");
  const [toRow, setToRow] = useState("");
  const [fromDatetime, setFromDatetime] = useState("");
  const [timestampField, setTimestampField] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFromRow(""); setToRow(""); setFromDatetime(""); setTimestampField(""); setPreview(null); };
  const body = () => ({
    from_row: fromRow ? Number(fromRow) : null,
    to_row: toRow ? Number(toRow) : null,
    from_datetime: fromDatetime || null,
    timestamp_field: timestampField || null,
  });
  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sheets/connections/${connectionId}/backfill/preview`, body());
      setPreview(data);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const runExecute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sheets/connections/${connectionId}/backfill/execute`,
        { ...body(), confirm: true });
      toast.success(`Backfill complete: ${data.created} created, ${data.attached} attached, ${data.duplicates} duplicates, ${data.errors.length} errors`);
      reset(); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Launch-day catch-up backfill</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            Recovers rows that arrived before this sheet's activation baseline. Bounded only —
            provide a starting row and/or a date/time boundary; nothing before it is ever touched.
            Every row still goes through the same dedup engine as live intake — replaying this is safe.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">From row (sheet row number)</Label>
              <Input type="number" min={2} value={fromRow} onChange={(e) => setFromRow(e.target.value)} placeholder="e.g. 9400" />
            </div>
            <div>
              <Label className="text-xs">To row (optional — defaults to the sheet's true last row)</Label>
              <Input type="number" min={2} value={toRow} onChange={(e) => setToRow(e.target.value)} placeholder="e.g. 9465" />
            </div>
            <div>
              <Label className="text-xs">From date/time (optional)</Label>
              <Input type="datetime-local" value={fromDatetime} onChange={(e) => setFromDatetime(e.target.value)} />
            </div>
          </div>
          {fromDatetime && (
            <div>
              <Label className="text-xs">Timestamp column (required with a date/time boundary)</Label>
              <Input value={timestampField} onChange={(e) => setTimestampField(e.target.value)} placeholder="e.g. Timestamp" />
            </div>
          )}
          {preview && !preview.error && (
            <div className="rounded-md border p-3 space-y-1 bg-muted/30" data-testid="sheet-backfill-preview">
              <p className="text-xs font-medium">Preview — nothing created yet</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-muted-foreground">Examined</p><p className="font-semibold">{preview.rows_examined}</p></div>
                <div><p className="text-muted-foreground">In range</p><p className="font-semibold">{preview.rows_in_range}</p></div>
                <div><p className="text-muted-foreground">Would create/attach</p><p className="font-semibold text-[hsl(var(--success))]">{preview.created}</p></div>
                <div><p className="text-muted-foreground">Duplicates</p><p className="font-semibold">{preview.duplicates}</p></div>
                <div><p className="text-muted-foreground">Unclassified</p><p className="font-semibold text-[hsl(var(--warning))]">{preview.unclassified}</p></div>
                <div><p className="text-muted-foreground">Errors</p><p className="font-semibold text-[hsl(var(--danger))]">{preview.errors?.length || 0}</p></div>
              </div>
              {preview.unparseable_timestamps > 0 && (
                <p className="text-[hsl(var(--warning))] text-xs">{preview.unparseable_timestamps} row(s) had an unparseable timestamp — included anyway, never silently dropped.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" disabled={busy || (!fromRow && !fromDatetime)} onClick={runPreview}>
            {busy ? "Working…" : "Preview"}
          </Button>
          <Button size="sm" disabled={busy || !preview || preview.error} onClick={runExecute} data-testid="sheet-backfill-execute">
            {busy ? "Working…" : "Execute — Admin confirms"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SheetConnectionCard = ({ c, onReload, onShowHistory }) => {
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [activating, setActivating] = useState(false);
  const [running, setRunning] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const loadDiag = async () => {
    setDiagBusy(true);
    try {
      const { data } = await api.get(`/sheets/connections/${c.connection_id}/diagnostics`);
      setDiag(data);
    } catch (e) { toast.error(errDetail(e)); } finally { setDiagBusy(false); }
  };
  const repairBaseline = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post(`/sheets/connections/${c.connection_id}/repair-baseline`);
      toast.success(`Baseline extended: row ${data.baseline_row_before} → ${data.baseline_row_after} (${data.rows_added_to_baseline} rows added, nothing imported)`);
      setDiag(null); onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setRepairing(false); }
  };

  const doScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.get(`/sheets/connections/${c.connection_id}/scan`);
      setScan(data);
    } catch (e) { toast.error(errDetail(e)); } finally { setScanning(false); }
  };
  const activate = async (baseline_choice) => {
    setActivating(true);
    try {
      const { data } = await api.post(`/sheets/connections/${c.connection_id}/activate`, { baseline_choice });
      toast.success(baseline_choice === "import_all"
        ? `Activated — imported ${data.import_result?.created ?? 0} new, ${data.import_result?.attached ?? 0} attached, ${data.import_result?.unclassified ?? 0} unclassified`
        : `Activated — ${data.baseline_rows} existing row(s) set as the starting point, only NEW rows from now on will import`);
      setScan(null); onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setActivating(false); }
  };
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/sheets/connections/${c.connection_id}/run`);
      toast.success(`Import: ${data.rows_total} rows → ${data.created} new, ${data.attached} attached, ${data.unclassified} unclassified, ${data.skipped} skipped, ${data.errors.length} errors`);
      onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setRunning(false); }
  };
  const toggleActive = async (active) => {
    try {
      await api.patch(`/sheets/connections/${c.connection_id}/active`, { active });
      toast.success(active ? "Lead intake enabled for this sheet" : "Lead intake disabled — the poller will skip this sheet");
      onReload();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Card data-testid={`sheet-conn-${c.connection_id}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <div className="flex-1 min-w-48">
            <p className="font-medium flex items-center gap-1.5">{c.tab} <span className="font-mono text-xs text-muted-foreground">({c.spreadsheet_id.slice(0, 18)}…)</span>
              {c.active === false && <Badge variant="outline" className="text-[9px]">Lead intake off</Badge>}
              {!c.intake_started && <Badge variant="outline" className="text-[9px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30">Not yet activated</Badge>}
              <Badge variant="outline" className="text-[9px]">{c.intake_mode === "mixed" ? "Mixed products" : "Single product"}</Badge>
            </p>
            <p className="text-xs text-muted-foreground">
              {c.mode} every {c.poll_minutes}m · {c.intake_mode === "mixed" ? `identifier: ${c.product_identifier_field}` : `product ${c.product_code || "from sheet"}`} ·
              last run {c.last_run_at ? `${fmtIST(c.last_run_at)} (${c.last_run_status}${c.last_run_summary ? `: ${c.last_run_summary.created} new / ${c.last_run_summary.unclassified ?? 0} unclassified / ${c.last_run_summary.skipped} skipped / ${c.last_run_summary.errors} errors` : ""})` : "never"}
            </p>
          </div>
          {c.intake_started ? (
            <>
              <div className="flex items-center gap-1.5">
                <Switch data-testid={`sheet-active-${c.connection_id}`} checked={c.active !== false}
                  onCheckedChange={toggleActive} />
                <Label className="text-xs">Lead intake enabled</Label>
              </div>
              <Button data-testid={`sheet-run-${c.connection_id}`} size="sm" variant="outline" className="h-7 text-xs" disabled={running || c.active === false} onClick={run}>
                <RefreshCcw className="h-3 w-3 mr-1" /> {running ? "Running…" : "Run now"}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onShowHistory(c.connection_id)}>History</Button>
              <Button data-testid={`sheet-backfill-open-${c.connection_id}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setBackfillOpen(true)}>Launch-day catch-up</Button>
              <SheetBackfillDialog connectionId={c.connection_id} open={backfillOpen} onOpenChange={setBackfillOpen} onDone={onReload} />
              <Button data-testid={`sheet-diag-${c.connection_id}`} size="sm" variant="ghost" className="h-7 text-xs" disabled={diagBusy} onClick={loadDiag}>
                {diagBusy ? "Checking…" : "Diagnostics"}
              </Button>
            </>
          ) : (
            <Button data-testid={`sheet-scan-${c.connection_id}`} size="sm" variant="outline" className="h-7 text-xs" disabled={scanning} onClick={doScan}>
              <Play className="h-3 w-3 mr-1" /> {scanning ? "Scanning…" : "Scan"}
            </Button>
          )}
        </div>

        <WebinarSourceBinding sourceType="google_sheet" sourceId={c.connection_id}
                              defaultProduct={c.product_code} />

        {diag && c.intake_started && (
          <div className="rounded-md border p-3 space-y-2 bg-muted/30" data-testid={`sheet-diag-result-${c.connection_id}`}>
            <p className="text-xs font-medium">Read-range diagnostics (2026-09 fix — no longer capped at 2,000 rows)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div><p className="text-muted-foreground">Sheet's true last row</p><p className="font-semibold">{diag.actual_last_row}</p></div>
              <div><p className="text-muted-foreground">Baseline / cursor row</p><p className="font-semibold">{diag.baseline_row}</p></div>
              <div><p className="text-muted-foreground">Unscanned gap</p><p className={`font-semibold ${diag.gap > 0 ? "text-[hsl(var(--warning))]" : ""}`}>{diag.gap}</p></div>
              <div><p className="text-muted-foreground">Auto-advance limit</p><p className="font-semibold">{diag.max_auto_advance_rows}</p></div>
            </div>
            {diag.needs_repair ? (
              <div className="flex items-center gap-2">
                <p className="text-xs text-[hsl(var(--warning))]">Gap exceeds the safety threshold — the automatic poller will refuse to run until repaired.</p>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={repairing} onClick={repairBaseline} data-testid={`sheet-repair-${c.connection_id}`}>
                  {repairing ? "Repairing…" : "Repair baseline (creates nothing)"}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-[hsl(var(--success))]">Healthy — normal polling is incremental and up to date.</p>
            )}
          </div>
        )}

        {c.intake_mode === "mixed" && c.intake_started && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground select-none">Product aliases for this incoming field ({c.product_identifier_field})</summary>
            <div className="mt-2"><ProductAliasMapper products={c._products || []} /></div>
          </details>
        )}

        {scan && !c.intake_started && (
          <div className="rounded-md border p-3 space-y-2 bg-muted/30" data-testid={`sheet-scan-result-${c.connection_id}`}>
            <p className="text-xs font-medium">Scan result — what would happen, nothing created yet</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              <div><p className="text-muted-foreground">Rows found</p><p className="font-semibold">{scan.rows_found}</p></div>
              <div><p className="text-muted-foreground">Already recognised</p><p className="font-semibold">{scan.already_recognised}</p></div>
              <div><p className="text-muted-foreground">Potential new leads</p><p className="font-semibold text-[hsl(var(--success))]">{scan.potential_new_leads}</p></div>
              <div><p className="text-muted-foreground">Unclassified product</p><p className="font-semibold text-[hsl(var(--warning))]">{scan.unclassified_product}</p></div>
              <div><p className="text-muted-foreground">Invalid/incomplete</p><p className="font-semibold text-[hsl(var(--danger))]">{scan.invalid_incomplete}</p></div>
            </div>
            <div className="flex gap-2 flex-wrap pt-1">
              <Button size="sm" data-testid={`sheet-activate-importall-${c.connection_id}`} disabled={activating} onClick={() => activate("import_all")}>
                {activating ? "Working…" : `Import All Non-Duplicates (${scan.potential_new_leads})`}
              </Button>
              <Button size="sm" variant="outline" data-testid={`sheet-activate-startnow-${c.connection_id}`} disabled={activating} onClick={() => activate("start_from_now")}>
                Start From Now — ignore existing rows
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const SheetsTab = () => {
  const [conns, setConns] = useState(null);
  const [sheet, setSheet] = useState("");
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("");
  const [mapRows, setMapRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState("");
  const [poll, setPoll] = useState("30");
  const [intakeMode, setIntakeMode] = useState("single");
  const [identifierField, setIdentifierField] = useState("");
  const [history, setHistory] = useState(null);
  const load = useCallback(() => api.get("/sheets/connections").then((r) => setConns(r.data)), []);
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)); }, [load]);

  const inspect = async () => {
    try {
      const { data } = await api.get(`/sheets/meta?spreadsheet=${encodeURIComponent(sheet)}`);
      setMeta(data); setTab(data.tabs[0] || "");
      // No automatic mapping by column name/similarity — every column
      // defaults to "Preserve only" until the admin explicitly maps it,
      // same rule as Meta's mapper.
      setMapRows((data.preview.headers || []).map((h) => ({
        key: h, label: h,
        sample: (data.preview.rows || [])[0]?.[h], target: "skip",
      })));
    } catch (e) { toast.error(errDetail(e)); }
  };
  const connect = async () => {
    if (intakeMode === "single" && !product) { toast.error("Pick a product for single-product mode"); return; }
    if (intakeMode === "mixed" && !identifierField) { toast.error("Pick which column identifies the product"); return; }
    try {
      const mapping = {};
      mapRows.forEach((r) => { if (r.target && r.target !== "skip") mapping[r.key] = r.target; });
      await api.post("/sheets/connections", {
        spreadsheet: sheet, tab, mapping,
        intake_mode: intakeMode,
        product_code: intakeMode === "single" ? product : null,
        product_identifier_field: intakeMode === "mixed" ? identifierField : null,
        mode: "poll", poll_minutes: Number(poll), active: true,
      });
      toast.success("Connection created — scan it below to choose how intake should start");
      setMeta(null); setSheet(""); setIntakeMode("single"); setIdentifierField(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const showHistory = async (id) => {
    const { data } = await api.get(`/sheets/connections/${id}/history`);
    setHistory({ id, runs: data });
  };
  if (!conns) return <Skeleton className="h-48" />;
  return (
    <div className="space-y-4" data-testid="sheets-admin">
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Google Sheets lead ingestion</CardTitle>
        <p className="text-xs text-muted-foreground">Sheets are an intake source, not a parallel CRM: every row flows through the canonical dedup engine — the same person never becomes a duplicate contact. Requires the Google Workspace connection (Integrations tab).</p>
      </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1 flex-1 min-w-64"><Label className="text-xs">Spreadsheet URL or ID</Label>
              <Input data-testid="sheet-url-input" className="h-9 font-mono text-xs" placeholder="https://docs.google.com/spreadsheets/d/…" value={sheet} onChange={(e) => setSheet(e.target.value)} /></div>
            <Button data-testid="sheet-inspect" className="h-9" disabled={!sheet.trim()} onClick={inspect}>Inspect</Button>
          </div>
          {meta && (
            <div className="space-y-3 p-3 rounded-md border">
              <div className="flex gap-2 flex-wrap">
                <div className="space-y-1"><Label className="text-xs">Worksheet / tab</Label>
                  <Select value={tab} onValueChange={setTab}>
                    <SelectTrigger data-testid="sheet-tab-select" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.tabs.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Lead type</Label>
                  <Select value={intakeMode} onValueChange={setIntakeMode}>
                    <SelectTrigger data-testid="sheet-intake-mode" className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{SHEET_INTAKE_MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
                {intakeMode === "single" ? (
                  <div className="space-y-1"><Label className="text-xs">Product</Label>
                    <Select value={product} onValueChange={setProduct}>
                      <SelectTrigger data-testid="sheet-product-select" className="h-8 w-56 text-xs"><SelectValue placeholder="Pick product" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                ) : (
                  <div className="space-y-1"><Label className="text-xs">Product identifier column</Label>
                    <Select value={identifierField} onValueChange={setIdentifierField}>
                      <SelectTrigger data-testid="sheet-identifier-select" className="h-8 w-56 text-xs"><SelectValue placeholder="Pick a column" /></SelectTrigger>
                      <SelectContent>{(meta.preview.headers || []).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select></div>
                )}
                <div className="space-y-1"><Label className="text-xs">Poll every</Label>
                  <Select value={poll} onValueChange={setPoll}>
                    <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{[["15", "15 min"], ["30", "30 min"], ["60", "1 hour"], ["240", "4 hours"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Column mapping — every column defaults to "Preserve only" until explicitly mapped; "name" is mandatory</Label>
                <FieldMapper rows={mapRows} onChange={setMapRows}
                  productCode={intakeMode === "single" ? (product || null) : null} universalTargets={SHEETS_UNIVERSAL_TARGETS} />
                <p className="text-[11px] text-muted-foreground">Preview: {(meta.preview.rows || []).length} sample rows loaded from “{meta.title}”.</p>
              </div>
              {intakeMode === "mixed" && <ProductAliasMapper products={products} />}
              <Button data-testid="sheet-connect" size="sm" disabled={!mapRows.some((r) => r.target === "name")} onClick={connect}>Create connection</Button>
              <p className="text-[11px] text-muted-foreground">This only creates the connection — nothing imports yet. Scan it below to choose Import Existing or Start From Now.</p>
            </div>
          )}
        </CardContent>
      </Card>
      {conns.map((c) => (
        <SheetConnectionCard key={c.connection_id} c={{ ...c, _products: products }} onReload={load} onShowHistory={showHistory} />
      ))}
      {history && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Import history</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {history.runs.map((r) => (
              <p key={r.run_id} className="text-xs">
                {fmtIST(r.started_at)} · {r.status === "baseline_repair_needed" ? "needs repair" : r.status}
                {r.status === "baseline_repair_needed" ? (
                  <span className="text-[hsl(var(--warning))]"> — {r.gap} unscanned rows beyond baseline row {r.baseline_row} (sheet's true last row: {r.actual_last_row})</span>
                ) : (
                  <>
                    {" "}· {r.rows_total} rows ({r.scan_range || "?"}, {r.rows_fetched ?? r.rows_total} fetched) → {r.created} created, {r.attached} attached, {r.unclassified ?? 0} unclassified, {r.skipped} skipped, {(r.errors || []).length} errors
                    {r.skip_reasons && r.skipped > 0 && (
                      <span className="text-muted-foreground"> (skipped: {Object.entries(r.skip_reasons).filter(([, n]) => n > 0).map(([k, n]) => `${k.replace(/_/g, " ")} ${n}`).join(", ")})</span>
                    )}
                    {(r.errors || []).slice(0, 2).map((e, i) => <span key={i} className="text-[hsl(var(--danger))]"> [row {e.row}: {e.error}]</span>)}
                  </>
                )}
              </p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ------------------------------ UAT FEEDBACK --------------------------- */
export const UatTab = () => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/uat/feedback").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try { await api.patch(`/uat/feedback/${id}`, { status }); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!items) return <Skeleton className="h-48" />;
  const FLOW = ["new", "reviewing", "accepted", "sent_to_developer", "fixed", "verified"];
  return (
    <Card data-testid="uat-admin">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">UAT feedback ({items.length})</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No feedback yet — employees report via the floating button.</p>}
        {items.map((f) => (
          <div key={f.feedback_id} className="p-2.5 rounded border space-y-1" data-testid={`uat-item-${f.feedback_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs">{f.feedback_id}</span>
              <Badge variant="outline" className="text-[9px] capitalize">{f.type.replace(/_/g, " ")}</Badge>
              <Badge variant="outline" className={`text-[9px] capitalize ${f.priority === "blocking" ? "text-[hsl(var(--danger))]" : f.priority === "major" ? "text-[hsl(var(--warning))]" : ""}`}>{f.priority}</Badge>
              <span className="text-xs text-muted-foreground">{f.employee} · {f.page_url} · {fmtIST(f.created_at)}</span>
              <Select value={f.status} onValueChange={(v) => setStatus(f.feedback_id, v)}>
                <SelectTrigger data-testid={`uat-status-${f.feedback_id}`} className="h-7 w-44 text-xs ml-auto"><SelectValue /></SelectTrigger>
                <SelectContent>{FLOW.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-sm">{f.description}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

/* ------------------------- WORKING DAYS & HOURS ------------------------- */
const WH_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const WH_DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

// Mirrors backend services.booking_schedule_conflicts — every bookable
// consultation window must fall inside staff working hours and not land in
// a break. Run client-side so both cards can warn (and block Save) before
// the round-trip; the backend re-checks and is the actual source of truth.
const scheduleConflicts = (slotSchedule, officeSchedule) => {
  const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
  const conflicts = [];
  for (const day of WH_DAY_KEYS) {
    const daySlots = slotSchedule?.[day];
    if (!daySlots || daySlots.length === 0) continue;
    const label = WH_DAY_LABELS[day];
    const dayCfg = officeSchedule?.[day];
    if (!dayCfg) { conflicts.push(`${label}: booking slots are set but staff aren't scheduled to work that day`); continue; }
    const openM = toMin(dayCfg.open), closeM = toMin(dayCfg.close);
    for (const [start, end] of daySlots) {
      const startM = toMin(start), endM = toMin(end);
      if (startM < openM || endM > closeM) {
        conflicts.push(`${label} ${start}–${end}: outside working hours (${dayCfg.open}–${dayCfg.close})`);
        continue;
      }
      for (const [bs, be] of dayCfg.breaks || []) {
        if (startM < toMin(be) && toMin(bs) < endM) conflicts.push(`${label} ${start}–${end}: overlaps a staff break (${bs}–${be})`);
      }
    }
  }
  return conflicts;
};

const ScheduleConflictWarning = ({ conflicts }) => {
  if (!conflicts.length) return null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 space-y-1" data-testid="schedule-conflict-warning">
      <p className="text-xs font-medium text-destructive flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Conflicts with working days & hours — fix these before saving:</p>
      <ul className="text-[11px] text-destructive/90 list-disc pl-5 space-y-0.5">
        {conflicts.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
    </div>
  );
};

const WorkingHoursCard = () => {
  const [schedule, setSchedule] = useState(null);
  const [slotSchedule, setSlotSchedule] = useState(null);
  useEffect(() => {
    api.get("/admin/working-hours").then((r) => setSchedule(r.data || {}));
    api.get("/admin/booking-slots").then((r) => setSlotSchedule(r.data || {}));
  }, []);
  if (!schedule || !slotSchedule) return <Skeleton className="h-24" />;
  const conflicts = scheduleConflicts(slotSchedule, schedule);
  const setDay = (day, cfg) => setSchedule({ ...schedule, [day]: cfg });
  const toggleOpen = (day, open) => setDay(day, open ? { open: "10:00", close: "18:00", breaks: [] } : null);
  const setField = (day, field, value) => setDay(day, { ...schedule[day], [field]: value });
  const addBreak = (day) => setDay(day, { ...schedule[day], breaks: [...(schedule[day].breaks || []), ["13:00", "13:30"]] });
  const updateBreak = (day, i, idx, value) => {
    const breaks = (schedule[day].breaks || []).map((b, bi) => bi === i ? (idx === 0 ? [value, b[1]] : [b[0], value]) : b);
    setDay(day, { ...schedule[day], breaks });
  };
  const removeBreak = (day, i) => setDay(day, { ...schedule[day], breaks: (schedule[day].breaks || []).filter((_, bi) => bi !== i) });
  const save = async () => {
    try { await api.put("/admin/working-hours", { schedule }); toast.success("Working days & hours saved"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Working days & hours</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-muted-foreground">Staff availability — governs consultation booking, human follow-up call tasks, and auto-computed task due-dates. AI voice calls and reminder messages use the separate, more relaxed "AI calling & reminder hours" window in the Voice card below (no days-off — just avoids the middle of the night). Booking slots (below) are directly tied to these hours — shrinking working hours here so an existing booking slot no longer fits will be blocked until the conflict is resolved.</p>
        <ScheduleConflictWarning conflicts={conflicts} />
        {WH_DAY_KEYS.map((day) => {
          const cfg = schedule[day];
          const open = !!cfg;
          return (
            <div key={day} className="border rounded-md p-2 space-y-1.5" data-testid={`wh-day-${day}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium w-24">{WH_DAY_LABELS[day]}</span>
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid={`wh-${day}-open`} checked={open} onCheckedChange={(v) => toggleOpen(day, v)} /> {open ? "Open" : "Closed"}
                </label>
                {open && (
                  <div className="flex items-center gap-1">
                    <Input data-testid={`wh-${day}-start`} type="time" className="h-8 w-28" value={cfg.open} onChange={(e) => setField(day, "open", e.target.value)} />
                    <span className="text-xs">–</span>
                    <Input data-testid={`wh-${day}-end`} type="time" className="h-8 w-28" value={cfg.close} onChange={(e) => setField(day, "close", e.target.value)} />
                  </div>
                )}
              </div>
              {open && (
                <div className="pl-1 space-y-1">
                  {(cfg.breaks || []).map((b, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground w-12">Break</span>
                      <Input type="time" className="h-7 w-24 text-xs" value={b[0]} onChange={(e) => updateBreak(day, i, 0, e.target.value)} />
                      <span className="text-xs">–</span>
                      <Input type="time" className="h-7 w-24 text-xs" value={b[1]} onChange={(e) => updateBreak(day, i, 1, e.target.value)} />
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => removeBreak(day, i)}>×</Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addBreak(day)}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add break</Button>
                </div>
              )}
            </div>
          );
        })}
        <Button data-testid="wh-save" size="sm" disabled={conflicts.length > 0} onClick={save}>Save working days & hours</Button>
      </CardContent>
    </Card>
  );
};

/* ------------------------- BOOKING CAPACITY PER PRODUCT ----------------- */
const CapacityGroupDialog = ({ open, onOpenChange, types, groups, editingGroup, onSaved }) => {
  const isEdit = !!editingGroup;
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("2");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(editingGroup?.name || "");
    setCapacity(String(editingGroup?.slot_capacity ?? 2));
    setSelected(new Set(editingGroup?.product_codes || []));
  }, [open, editingGroup]);
  const otherGroupOf = (code) => groups.find((g) => g.group_id !== editingGroup?.group_id && g.product_codes.includes(code));
  const toggle = (code) => setSelected((s) => {
    const next = new Set(s);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const codes = [...selected];
    if (codes.length < 2) { toast.error("Pick at least 2 products to share a pool"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), product_codes: codes, slot_capacity: Number(capacity) };
      if (isEdit) await api.patch(`/appointments/capacity-groups/${editingGroup.group_id}`, body);
      else await api.post("/appointments/capacity-groups", body);
      toast.success(isEdit ? "Group updated" : "Group created");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/appointments/capacity-groups/${editingGroup.group_id}`);
      toast.success("Group removed — its products are now standalone");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="capacity-group-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Manage capacity group" : "Create capacity group"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Group name</Label>
            <Input data-testid="cgd-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Skill Migration (In-house + MARA)" /></div>
          <div className="space-y-1"><Label className="text-xs">Shared max overlapping bookings</Label>
            <Input data-testid="cgd-capacity" type="number" min="1" className="w-32" value={capacity} onChange={(e) => setCapacity(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Products sharing this pool</Label>
            <div className="border rounded-md p-2 space-y-1 max-h-56 overflow-y-auto">
              {types.map((t) => {
                const other = otherGroupOf(t.product_code);
                return (
                  <label key={t.product_code} className={`flex items-center gap-2 text-sm ${other ? "opacity-50" : ""}`}>
                    <Checkbox data-testid={`cgd-pick-${t.product_code}`} disabled={!!other}
                      checked={selected.has(t.product_code)} onCheckedChange={() => toggle(t.product_code)} />
                    {t.product_code} <span className="text-xs text-muted-foreground">({t.name})</span>
                    {other && <span className="text-[10px] text-muted-foreground">— in "{other.name}"</span>}
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">Pick 2 or more. Leave a product unchecked to keep it on its own individual quota.</p>
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {isEdit ? <Button data-testid="cgd-delete" type="button" variant="destructive" size="sm" disabled={saving} onClick={remove}>Delete group</Button> : <span />}
          <Button data-testid="cgd-save" size="sm" disabled={saving} onClick={save}>{isEdit ? "Save changes" : "Create group"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProductCapacityCard = () => {
  const [types, setTypes] = useState(null);
  const [groups, setGroups] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const load = useCallback(() => {
    Promise.all([api.get("/appointments/types"), api.get("/appointments/capacity-groups")]).then(([tr, gr]) => {
      const active = (tr.data || []).filter((t) => t.active);
      setTypes(active);
      setGroups(gr.data || []);
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!types) return <Skeleton className="h-32" />;

  const grouped = new Set(groups.flatMap((g) => g.product_codes));
  const groupRows = groups.map((g) => ({
    key: `group:${g.group_id}`, group_id: g.group_id, isGroup: true,
    label: g.name, sub: g.product_codes.join(" + "), capacity: g.slot_capacity,
  }));
  const singleRows = types.filter((t) => !grouped.has(t.product_code)).map((t) => ({
    key: t.appointment_type_id, appointment_type_id: t.appointment_type_id, isGroup: false,
    label: t.product_code, sub: t.name, capacity: t.slot_capacity ?? "",
  }));
  const rows = [...groupRows, ...singleRows];
  const edited = { ...Object.fromEntries(rows.map((r) => [r.key, r.capacity ?? ""])), ...edits };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(rows.map((r) => {
        const raw = edited[r.key];
        if (r.isGroup) {
          const val = Number(raw);
          if (!val || val === r.capacity) return null;
          return api.patch(`/appointments/capacity-groups/${r.group_id}`, { slot_capacity: val });
        }
        const val = raw === "" || raw === null || raw === undefined ? null : Number(raw);
        if (val === (r.capacity || null)) return null;
        return api.patch(`/appointments/types/${r.appointment_type_id}/capacity`, { slot_capacity: val });
      }));
      toast.success("Booking capacity saved");
      setEdits({});
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  return (
    <Card><CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-heading">Booking capacity per product</CardTitle>
        <Button data-testid="cap-new-group" size="sm" variant="outline" onClick={() => { setEditingGroup(null); setDialogOpen(true); }}>
          <Plus className="h-3.5 w-3.5 mr-1" />New shared-pool group</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          Every bookable product draws consultation slots from a pool — either its own individual quota, or a pool shared with other products. This table lists every pool: which product(s) are in it, and the maximum number of overlapping bookings allowed for that pool at the same date and time (i.e. how many counsellors/seats can run that consultation type at once). A pool can hold just one product on its own quota, or 2+ products sharing a single combined quota — e.g. two products that are really the same lead pre-signup, or simply share counsellor capacity. Use "New shared-pool group" to pool 2+ products together, or "Manage" on an existing pool to add/remove products or change its shared capacity. Every active, directly-bookable product (created or activated under Administration → Commercial plans) gets its own row automatically — leave its capacity blank to use the default below, or set a number to override it.
        </p>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Product(s)</TableHead>
            <TableHead>Consultation type</TableHead>
            <TableHead className="w-44">Max overlapping bookings</TableHead>
            <TableHead className="w-24" />
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} data-testid={`cap-row-${r.isGroup ? r.group_id : r.label}`}>
                <TableCell className="font-medium">{r.isGroup ? r.sub : r.label}</TableCell>
                <TableCell className="text-sm">{r.isGroup ? <Badge variant="outline" className="text-[10px]">shared pool</Badge> : r.sub}</TableCell>
                <TableCell>
                  <Input data-testid={`cap-input-${r.isGroup ? r.group_id : r.label}`} type="number" min="1" className="h-8 w-28"
                    placeholder={r.isGroup ? undefined : "Default"}
                    value={edited[r.key] ?? ""}
                    onChange={(e) => setEdits({ ...edits, [r.key]: e.target.value })} />
                </TableCell>
                <TableCell>
                  {r.isGroup && (
                    <Button data-testid={`cap-manage-${r.group_id}`} size="sm" variant="ghost" className="h-7 text-xs"
                      onClick={() => { setEditingGroup(groups.find((g) => g.group_id === r.group_id)); setDialogOpen(true); }}>
                      Manage</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button data-testid="cap-save" size="sm" disabled={saving} onClick={save}>Save booking capacity</Button>
      </CardContent>
      <CapacityGroupDialog open={dialogOpen} onOpenChange={setDialogOpen} types={types} groups={groups}
        editingGroup={editingGroup} onSaved={load} />
    </Card>
  );
};

/* ------------------------- BOOKING SLOTS -------------------------------- */
const BS_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const BS_DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

const BookingSlotsCard = () => {
  const [schedule, setSchedule] = useState(null);
  const [officeSchedule, setOfficeSchedule] = useState(null);
  useEffect(() => {
    api.get("/admin/booking-slots").then((r) => setSchedule(r.data || {}));
    api.get("/admin/working-hours").then((r) => setOfficeSchedule(r.data || {}));
  }, []);
  if (!schedule || !officeSchedule) return <Skeleton className="h-24" />;
  const conflicts = scheduleConflicts(schedule, officeSchedule);
  const setDay = (day, slots) => setSchedule({ ...schedule, [day]: slots });
  const toggleOpen = (day, open) => setDay(day, open ? [["12:15", "13:00"]] : null);
  const addSlot = (day) => setDay(day, [...(schedule[day] || []), ["12:15", "13:00"]]);
  const updateSlot = (day, i, idx, value) => {
    const slots = (schedule[day] || []).map((s, si) => si === i ? (idx === 0 ? [value, s[1]] : [s[0], value]) : s);
    setDay(day, slots);
  };
  const removeSlot = (day, i) => setDay(day, (schedule[day] || []).filter((_, si) => si !== i));
  const save = async () => {
    try { await api.put("/admin/booking-slots", { schedule }); toast.success("Booking slots saved"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Booking slots</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-muted-foreground">The exact consultation windows customers (and the voice agent) can pick from — a specific business decision, not just "whenever staff are in," so it's kept as its own schedule rather than reusing "Working days & hours" above. But every slot here must still fall inside working hours and outside any break — conflicts are blocked until resolved.</p>
        <ScheduleConflictWarning conflicts={conflicts} />
        {BS_DAY_KEYS.map((day) => {
          const slots = schedule[day];
          const open = !!slots && slots.length > 0;
          return (
            <div key={day} className="border rounded-md p-2 space-y-1.5" data-testid={`bs-day-${day}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium w-24">{BS_DAY_LABELS[day]}</span>
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid={`bs-${day}-open`} checked={open} onCheckedChange={(v) => toggleOpen(day, v)} /> {open ? "Bookable" : "No booking"}
                </label>
              </div>
              {open && (
                <div className="pl-1 space-y-1">
                  {slots.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Input data-testid={`bs-${day}-${i}-start`} type="time" className="h-7 w-24 text-xs" value={s[0]} onChange={(e) => updateSlot(day, i, 0, e.target.value)} />
                      <span className="text-xs">–</span>
                      <Input data-testid={`bs-${day}-${i}-end`} type="time" className="h-7 w-24 text-xs" value={s[1]} onChange={(e) => updateSlot(day, i, 1, e.target.value)} />
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => removeSlot(day, i)}>×</Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addSlot(day)}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add slot</Button>
                </div>
              )}
            </div>
          );
        })}
        <Button data-testid="bs-save" size="sm" disabled={conflicts.length > 0} onClick={save}>Save booking slots</Button>
      </CardContent>
    </Card>
  );
};

/* ------------------------- DIGEST + VOICE + CALL POLICY ---------------- */
export const OpsConfigTab = () => {
  const [digest, setDigest] = useState(null);
  const [voice, setVoice] = useState(null);
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    api.get("/admin/digest-config").then((r) => setDigest({ enabled: true, time: "08:30", roles: ["admin", "manager", "supervisor"], include_weekends: false, ...r.data }));
    api.get("/admin/voice-settings").then((r) => setVoice(r.data));
    api.get("/calls/policy").then((r) => setPolicy(r.data));
  }, []);
  const saveDigest = async () => {
    try { await api.put("/admin/digest-config", digest); toast.success("Digest configuration saved"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const previewDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest-preview");
      toast.success(`Preview (not sent): ${(data.sections || []).map((s) => `${s.label}: ${s.count}`).join("; ") || "all clear"}`);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const saveVoice = async () => {
    try { await api.put("/admin/voice-settings", { settings: voice }); toast.success("Voice agent settings saved"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!digest || !voice || !policy) return <Skeleton className="h-64" />;
  return (
    <div className="space-y-4" data-testid="opsconfig-admin">
      <WorkingHoursCard />
      <BookingSlotsCard />
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Daily manager digest</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm pb-2">
              <Switch data-testid="digest-enabled" checked={digest.enabled} onCheckedChange={(v) => setDigest({ ...digest, enabled: v })} /> Enabled
            </label>
            <div className="space-y-1"><Label className="text-xs">Delivery time (IST)</Label>
              <Input data-testid="digest-time" type="time" className="h-9 w-28" value={digest.time} onChange={(e) => setDigest({ ...digest, time: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-xs pb-2">
              <Checkbox checked={digest.include_weekends} onCheckedChange={(v) => setDigest({ ...digest, include_weekends: !!v })} /> Include weekends
            </label>
            <div className="space-y-1"><Label className="text-xs">Recipient roles</Label>
              <div className="flex gap-2">
                {["admin", "manager", "supervisor", "process"].map((r) => (
                  <label key={r} className="flex items-center gap-1 text-xs">
                    <Checkbox checked={digest.roles.includes(r)} onCheckedChange={(v) => setDigest({ ...digest, roles: v ? [...digest.roles, r] : digest.roles.filter((x) => x !== r) })} /> {r}
                  </label>
                ))}
              </div></div>
            <Button data-testid="digest-save" size="sm" className="h-9" onClick={saveDigest}>Save</Button>
            <Button data-testid="digest-preview" size="sm" variant="outline" className="h-9" onClick={previewDigest}>Generate now (preview)</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Delivered in-app to recipient roles; email delivery activates automatically once Google Workspace email is connected.</p>
        </CardContent>
      </Card>

      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Booking automations, reminders &amp; calling hours</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Genuinely shared settings — Finance payment reminders and ordinary booking automations use these too, not
            Voice alone, so they stay here as the one editable copy. Voice-only controls (max attempts, retry delay,
            concurrency, stale timeout) moved to Administration → Voice &amp; ElevenLabs.
          </p>
          <div className="space-y-1 max-w-xs"><Label className="text-xs">AI calling &amp; reminder hours (IST)</Label>
            <div className="flex items-center gap-1">
              <Input data-testid="voice-window-start" type="time" className="h-9" value={voice["chase.call_window"]?.start || "10:00"}
                onChange={(e) => setVoice({ ...voice, "chase.call_window": { ...voice["chase.call_window"], start: e.target.value } })} />
              <span className="text-xs">–</span>
              <Input data-testid="voice-window-end" type="time" className="h-9" value={voice["chase.call_window"]?.end || "21:30"}
                onChange={(e) => setVoice({ ...voice, "chase.call_window": { ...voice["chase.call_window"], end: e.target.value } })} />
            </div></div>
          <p className="text-[11px] text-muted-foreground">Applies every day of the week, including weekends and staff days off — no day-off concept here, it just bounds the hour of day so AI voice calls and reminder messages never fire in the middle of the night.</p>
          <div className="rounded-md border p-3 space-y-2">
            <p className="text-xs font-medium">Default booking automations (independently switchable — no code change)</p>
            {[["booking.auto_invitation", "Booking invitation + self-booking link on new lead"],
              ["reminders.prev_day_enabled", "Previous-day consultation reminder (21:00)"],
              ["reminders.same_day_enabled", "Same-day consultation reminder (09:00)"],
              ["reminders.t60_enabled", "T-60 minutes important reminder"],
              ["booking.auto_cancel_unassigned_enabled", "Auto-cancel + leadership alert if still unassigned at T-60"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Switch data-testid={`auto-${k.replaceAll(".", "-")}`} checked={voice[k] !== false} onCheckedChange={(v) => setVoice({ ...voice, [k]: v })} />
                {l}
              </label>
            ))}
            <div className="space-y-1"><Label className="text-xs">No-booking wait before voice chase (minutes)</Label>
              <Input data-testid="booking-wait" type="number" className="h-9 w-40" value={voice["booking.self_booking_wait_minutes"] ?? 60}
                onChange={(e) => setVoice({ ...voice, "booking.self_booking_wait_minutes": Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="text-xs">Default max overlapping bookings per exact time slot</Label>
              <Input data-testid="slot-capacity" type="number" min="1" className="h-9 w-40" value={voice["booking.slot_capacity"] ?? 3}
                onChange={(e) => setVoice({ ...voice, "booking.slot_capacity": Number(e.target.value) })} />
              <p className="text-[11px] text-muted-foreground">How many different customers can book the exact same date + time before it's shown as full — applies to any product without its own override in the "Booking capacity per product" card below.</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Flow: booking invitation → wait → check booked → Attempt 1 → gap + re-check → Attempt 2 → human handover. Any successful booking suppresses pending attempts. The real-lead AI Voice master switch and Voice-only operational controls live in Administration → Voice &amp; ElevenLabs.</p>
          <Button data-testid="voice-save" size="sm" onClick={saveVoice}>Save voice settings</Button>
        </CardContent>
      </Card>

      <ProductCapacityCard />

      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Call disposition policy</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          <p className="text-xs text-muted-foreground mb-2">Not-connected calls require a disposition; retryable ones auto-schedule 2 follow-ups (one on Saturday), snapped onto working days & hours. Terminal dispositions never schedule retries.</p>
          {policy.dispositions.map((d) => (
            <p key={d.key} className="text-xs">
              <b>{d.label}</b>
              {d.terminal && <Badge variant="outline" className="text-[9px] ml-1">no retries</Badge>}
              {d.note_required && <Badge variant="outline" className="text-[9px] ml-1">note required</Badge>}
            </p>
          ))}
          <p className="text-[11px] text-muted-foreground pt-1">Retry gaps: {(policy.policy.retry_gap_days || []).join(" & ")} days · connected calls require note + intent.</p>
        </CardContent>
      </Card>

    </div>
  );
};
